import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import {
  Home,
  Users,
  Settings,
  FileText,
  LogOut,
  BarChart3,
  Link as LinkIcon,
  ChevronRight,
  ChevronDown,
  Video,
  Mail,
  Filter,
  PieChart,
  Tag,
  Activity,
  GraduationCap,
  Truck,
} from 'lucide-react';

interface SidebarChild {
  to: string;
  label: string;
  icon: React.ElementType;
  permission?: string;
}

interface SidebarGroup {
  key: string;
  label: string;
  icon: React.ElementType;
  children: SidebarChild[];
}

const menuGroups: SidebarGroup[] = [
  {
    key: 'analytics',
    label: 'Analytics',
    icon: BarChart3,
    children: [
      { to: '/kommo', label: "Vue d'ensemble", icon: PieChart, permission: 'view kommo analytics' },
      { to: '/kommo/leads', label: 'Leads', icon: FileText, permission: 'view kommo analytics' },
      { to: '/kommo/sellers', label: 'Commerciaux', icon: Activity, permission: 'view kommo analytics' },
      { to: '/kommo/tags', label: 'Tags', icon: Tag, permission: 'view kommo analytics' },
    ],
  },
  {
    key: 'dispatch',
    label: 'Dispatch',
    icon: Truck,
    children: [
      { to: '/logs', label: 'Logs', icon: FileText, permission: 'view logs' },
      { to: '/settings', label: 'Parametres', icon: Settings, permission: 'view settings' },
      { to: '/users', label: 'Commerciaux', icon: Users, permission: 'view users' },
    ],
  },
  {
    key: 'classe',
    label: 'Classe',
    icon: Video,
    children: [
      { to: '/virtual-classes', label: 'Classe virtuelle', icon: Video, permission: 'view virtual classes' },
      { to: '/virtual-classes/access-stats', label: 'Stats Acces Classes', icon: BarChart3, permission: 'view virtual classes' },
      { to: '/settings/email-templates', label: 'Modeles Emails', icon: Mail, permission: 'view settings' },
      { to: '/scheduled-emails', label: 'Emails programmes', icon: Mail, permission: 'view scheduled emails' },
      { to: '/email-filter', label: 'Filtrage Emails', icon: Filter, permission: 'view settings' },
    ],
  },
  {
    key: 'learnybox',
    label: 'Learnybox',
    icon: GraduationCap,
    children: [
      { to: '/payments-links', label: 'Liens de paiement', icon: LinkIcon, permission: 'view payments' },
      { to: '/inscrits-formation', label: 'Inscrits formation', icon: Users, permission: 'view inscrits formation' },
    ],
  },
  {
    key: 'settings',
    label: 'Settings',
    icon: Settings,
    children: [
      { to: '/admin/users', label: 'Utilisateurs', icon: Users, permission: 'manage users' },
      { to: '/admin/roles', label: 'Roles', icon: Settings, permission: 'manage roles' },
    ],
  },
];

function Sidebar() {
  const location = useLocation();
  const { logout, user, hasPermission } = useAuth();
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => {
    // Auto-open the group containing the current route
    const initial = new Set<string>();
    for (const group of menuGroups) {
      if (group.children.some((child) => location.pathname === child.to || location.pathname.startsWith(child.to + '/'))) {
        initial.add(group.key);
      }
    }
    return initial;
  });

  const toggleGroup = (key: string) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const getVisibleChildren = (group: SidebarGroup) =>
    group.children.filter((child) => !child.permission || hasPermission(child.permission));

  return (
    <div className="w-64 bg-white border-r border-slate-200 h-screen flex flex-col fixed left-0 top-0 overflow-y-auto z-20">
      {/* Header */}
      <div className="p-5">
        <div className="flex items-center gap-2.5">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary">
            <BarChart3 className="h-4 w-4 text-primary-foreground" />
          </div>
          <div className="flex flex-col">
            <h2 className="text-sm font-bold text-slate-900 tracking-tight leading-none">AMC Lead</h2>
            <span className="text-[10px] font-semibold text-slate-400 mt-0.5 uppercase tracking-wider">Dispatch v2.1</span>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-2 space-y-0.5 overflow-y-auto">
        {/* Dashboard - always top-level */}
        {hasPermission('view dashboard') && (
          <Link
            to="/"
            className={cn(
              "group flex items-center justify-between gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150",
              location.pathname === '/'
                ? "bg-primary/5 text-primary"
                : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
            )}
          >
            <div className="flex items-center gap-2.5">
              <Home className={cn("h-4 w-4 transition-colors", location.pathname === '/' ? "text-primary" : "text-slate-400 group-hover:text-slate-600")} />
              Tableau de bord
            </div>
            {location.pathname === '/' && <ChevronRight className="h-3 w-3 opacity-40" />}
          </Link>
        )}

        {/* Collapsible Groups */}
        {menuGroups.map((group) => {
          const visibleChildren = getVisibleChildren(group);
          if (visibleChildren.length === 0) return null;

          const isOpen = openGroups.has(group.key);
          const hasActiveChild = visibleChildren.some(
            (child) => location.pathname === child.to || location.pathname.startsWith(child.to + '/')
          );
          const GroupIcon = group.icon;

          return (
            <div key={group.key} className="mt-1">
              <button
                onClick={() => toggleGroup(group.key)}
                className={cn(
                  "w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 group",
                  hasActiveChild && !isOpen
                    ? "text-primary bg-primary/5"
                    : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                )}
              >
                <div className="flex items-center gap-2.5">
                  <GroupIcon className={cn(
                    "h-4 w-4 transition-colors",
                    hasActiveChild ? "text-primary" : "text-slate-400 group-hover:text-slate-600"
                  )} />
                  {group.label}
                </div>
                <ChevronDown className={cn(
                  "h-3.5 w-3.5 text-slate-400 transition-transform duration-200",
                  isOpen && "rotate-180"
                )} />
              </button>

              {isOpen && (
                <div className="ml-3 pl-3 border-l border-slate-100 mt-0.5 space-y-0.5">
                  {visibleChildren.map((child) => {
                    const Icon = child.icon;
                    const isActive = location.pathname === child.to;
                    return (
                      <Link
                        key={child.to}
                        to={child.to}
                        className={cn(
                          "group flex items-center justify-between gap-2.5 px-3 py-1.5 rounded-lg text-[13px] font-medium transition-all duration-150",
                          isActive
                            ? "bg-primary/5 text-primary"
                            : "text-slate-500 hover:text-slate-900 hover:bg-slate-50"
                        )}
                      >
                        <div className="flex items-center gap-2">
                          <Icon className={cn("h-3.5 w-3.5 transition-colors", isActive ? "text-primary" : "text-slate-400 group-hover:text-slate-500")} />
                          {child.label}
                        </div>
                        {isActive && <ChevronRight className="h-3 w-3 opacity-40" />}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Profile & Footer */}
      <div className="p-3 mt-auto border-t border-slate-100">
        {user && (
          <Link to="/profile" className="mb-3 p-3 bg-slate-50 rounded-lg block hover:bg-slate-100 transition-colors group/profile">
            <div className="flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-full bg-white border border-slate-200 flex items-center justify-center text-primary font-semibold text-sm group-hover/profile:border-primary/30 transition-colors">
                {user.username?.charAt(0) || 'U'}
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-xs font-semibold text-slate-900 truncate">{user.username}</span>
                <span className="text-[9px] font-medium text-slate-400 uppercase tracking-wide truncate">Admin</span>
              </div>
              <Settings className="ml-auto h-3 w-3 text-slate-300 opacity-0 group-hover/profile:opacity-100 transition-opacity" />
            </div>
          </Link>
        )}

        <button
          onClick={handleLogout}
          className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium text-slate-500 hover:text-red-600 hover:bg-red-50 w-full transition-all duration-150 group"
        >
          <LogOut className="h-4 w-4 text-slate-400 group-hover:text-red-500 transition-colors" />
          Se deconnecter
        </button>
      </div>
    </div>
  );
}

export default Sidebar;
